// SAME IMPORTS — NO CHANGE
import React, { useReducer, useEffect, useRef, useState } from "react";
import { Container, Row, Col, Form, Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGoogle } from "@fortawesome/free-brands-svg-icons"; // Changed to free-brands-svg-icons
import { faRedo, faClock } from "@fortawesome/free-solid-svg-icons"; // Keep solid icons separate
import { NavLink, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  loginRequest,
  loginSuccess,
  signupRequest,
  loginFailure,
  signupFailure,
  getDesignationsRequest,
} from "../../redux/reducer/AuthReducer";
import { toast } from "react-toastify";
import { signInWithPopup } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, provider, db } from "../../firebase";
import "../../style.scss";

// Format time function
const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

// INITIAL STATE
const initialState = {
  mail: "",
  pass: "",
  name: "",
  mob: "",
  dept: "",
  desg: "",
  conPass: "",
  emp_id: "",
  selectedDeptIndex: null,
  designations: [],
  firebaseErr: "",
  isLogin: true,
  errors: {},
  otp: "",
  showOtpField: false,
  isOtpSent: false,
  isLoading: false,
  otpSentTime: null,
};

// REDUCER
const formReducer = (state, action) => {
  switch (action.type) {
    case "UPDATE_FIELD":
      return {
        ...state,
        [action.field]: action.value,
        errors: { ...state.errors, [action.field]: "" },
      };
    case "SET_FIREBASE_ERROR":
      return { ...state, firebaseErr: action.error };
    case "TOGGLE_LOGIN":
      return { ...initialState, isLogin: !state.isLogin };
    case "RESET_FORM":
      return { ...initialState, isLogin: state.isLogin };
    case "SET_ERRORS":
      return { ...state, errors: action.errors };
    case "SUBMIT_START":
      return { ...state, isLoading: true };
    case "SUBMIT_END":
      return { ...state, isLoading: false };
    case "SET_OTP_SENT":
      return { 
        ...state, 
        isOtpSent: true, 
        showOtpField: true,
        otpSentTime: Date.now()
      };
    case "RESET_OTP_FLOW":
      return {
        ...state,
        showOtpField: false,
        isOtpSent: false,
        otp: "",
        errors: { ...state.errors, otp: "" },
        otpSentTime: null
      };
    case "SET_OTP_SENT_TIME":
      return {
        ...state,
        otpSentTime: action.time
      };
    default:
      return state;
  }
};

// COMPONENT
export default function Login() {
  const [state, dispatch] = useReducer(formReducer, initialState);
  const {
    mail,
    pass,
    name,
    mob,
    dept,
    desg,
    conPass,
    emp_id,
    firebaseErr,
    isLogin,
    errors,
    otp,
    showOtpField,
    isOtpSent,
    isLoading,
    otpSentTime,
  } = state;

  const reduxDispatch = useDispatch();
  const navigate = useNavigate();
  const { error, loginResponse, signupResponse, designations } = useSelector(
    (state) => state.AuthReducer
  );
  const shownErrorRef = useRef("");

  const [departments, setDepartments] = useState([]);
  const [loadingDept, setLoadingDept] = useState(false);
  const [deptError, setDeptError] = useState("");
  const [resendTimer, setResendTimer] = useState(0);
  const [canResend, setCanResend] = useState(false);
  const timerRef = useRef(null);

  // Timer function for resend OTP
  const startCountdown = (seconds) => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    
    setResendTimer(seconds);
    setCanResend(false);
    
    timerRef.current = setInterval(() => {
      setResendTimer(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          setCanResend(true);
          localStorage.removeItem('otpSentTime');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Timer function to initialize from localStorage
  const initializeTimerFromStorage = () => {
    const storedTime = localStorage.getItem('otpSentTime');
    if (storedTime) {
      const now = Date.now();
      const storedTimestamp = parseInt(storedTime);
      const TEN_MINUTES_MS = 10 * 60 * 1000;
      const timePassed = now - storedTimestamp;
      const timeRemaining = Math.max(0, TEN_MINUTES_MS - timePassed);
      const secondsRemaining = Math.floor(timeRemaining / 1000);
      
      if (secondsRemaining > 0) {
        startCountdown(secondsRemaining);
        dispatch({ type: "SET_OTP_SENT_TIME", time: storedTimestamp });
        dispatch({ type: "UPDATE_FIELD", field: "showOtpField", value: true });
        dispatch({ type: "UPDATE_FIELD", field: "isOtpSent", value: true });
      } else {
        localStorage.removeItem('otpSentTime');
        setCanResend(true);
      }
    }
  };

  // Fetch departments on load
  useEffect(() => {
    const fetchDepartments = async () => {
      setLoadingDept(true);
      try {
        const resp = await fetch(
          "https://dotconnect.dotandkey.com/api/v1/department/all"
        );
        if (!resp.ok) throw new Error(`Status ${resp.status}`);
        const data = await resp.json();
        setDepartments(data.data || []);
      } catch (err) {
        console.error("Failed to load departments:", err);
        setDeptError("Failed to load departments");
      } finally {
        setLoadingDept(false);
      }
    };
    
    fetchDepartments();
    initializeTimerFromStorage();
    
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  // Fetch designations on department change
  const handleDepartmentChange = (e) => {
    const deptId = e.target.value;

    dispatch({ type: "UPDATE_FIELD", field: "dept", value: deptId });
    dispatch({ type: "UPDATE_FIELD", field: "desg", value: "" });

    if (deptId) {
      reduxDispatch(getDesignationsRequest(deptId));
    }
  };

  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // Send OTP API call
  const sendOtp = async () => {
    try {
      dispatch({ type: "SUBMIT_START" });
      
      const response = await fetch('https://dotconnect.dotandkey.com/api/v1/auth/send-otp', {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: mail.toLowerCase()
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        const currentTime = Date.now();
        localStorage.setItem('otpSentTime', currentTime.toString());
        dispatch({ type: "SET_OTP_SENT" });
        startCountdown(10 * 60); // 10 minutes = 600 seconds
        toast.success("OTP sent successfully! Check your email.");
      } else {
        throw new Error(data.message || "Failed to send OTP");
      }
    } catch (error) {
      console.error("OTP sending error:", error);
      toast.error(error.message || "Failed to send OTP. Please try again.");
    } finally {
      dispatch({ type: "SUBMIT_END" });
    }
  };

  // Resend OTP function with timer
  const handleResendOtp = async () => {
    if (!canResend) return;
    
    try {
      dispatch({ type: "SUBMIT_START" });
      
      const response = await fetch('https://dotconnect.dotandkey.com/api/v1/auth/send-otp', {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: mail.toLowerCase()
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        const currentTime = Date.now();
        localStorage.setItem('otpSentTime', currentTime.toString());
        startCountdown(10 * 60); // Reset to 10 minutes
        toast.success("OTP resent successfully!");
      } else {
        throw new Error(data.message || "Failed to resend OTP");
      }
    } catch (error) {
      console.error("OTP resend error:", error);
      toast.error(error.message || "Failed to resend OTP. Please try again.");
    } finally {
      dispatch({ type: "SUBMIT_END" });
    }
  };

  // Complete registration with OTP
  const completeRegistration = async () => {
    const payload = {
      name,
      email: mail.toLowerCase(),
      mobile_no: mob,
      emp_id: emp_id || `EMP${Date.now().toString().slice(-6)}`,
      DepartmentID: dept,
      DesignationID: desg,
      password: pass,
      confirmPassword: conPass,
      is_admin: false,
      otp: otp
    };

    dispatch({ type: "SUBMIT_START" });

    setTimeout(async () => {
      try {
        await reduxDispatch(signupRequest(payload));
      } finally {
        dispatch({ type: "SUBMIT_END" });
      }
    }, 2000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = {};

    if (isLogin) {
      // LOGIN VALIDATION
      if (!mail.trim()) newErrors.mail = "Please enter your Email Id";
      else if (!regex.test(mail))
        newErrors.mail = "Please enter a correct Email Id!";

      if (!pass.trim()) newErrors.pass = "Please enter password";
      else if (pass.length < 8)
        newErrors.pass = "Password must be at least 8 characters";

      if (Object.keys(newErrors).length) {
        dispatch({ type: "SET_ERRORS", errors: newErrors });
        return;
      }

      dispatch({ type: "SUBMIT_START" });

      setTimeout(async () => {
        try {
          await reduxDispatch(
            loginRequest({ email: mail.toLowerCase(), password: pass })
          );
        } finally {
          dispatch({ type: "SUBMIT_END" });
        }
      }, 1000);
    } else {
      // SIGNUP VALIDATION (for both Send OTP and Register)
      if (!name.trim()) newErrors.name = "Please enter your name";
      if (!mail.trim()) newErrors.mail = "Please enter your Email Id";
      else if (!regex.test(mail)) newErrors.mail = "Please enter a valid email";
      if (!mob || mob.toString().length < 10)
        newErrors.mob = "Please enter a valid mobile number";
      if (!dept.trim()) newErrors.dept = "Please select a department";
      if (!desg.trim()) newErrors.desg = "Please select a designation";
      if (!pass.trim()) newErrors.pass = "Please enter password";
      else if (pass.length < 8)
        newErrors.pass = "Password must be at least 8 characters";
      if (!conPass.trim()) newErrors.conPass = "Please confirm password";
      else if (pass !== conPass) newErrors.conPass = "Passwords do not match";

      if (Object.keys(newErrors).length) {
        dispatch({ type: "SET_ERRORS", errors: newErrors });
        return;
      }

      // If OTP field is shown, validate OTP and complete registration
      if (showOtpField) {
        if (!otp.trim() || otp.length !== 6) {
          newErrors.otp = "Please enter a valid 6-digit OTP";
          dispatch({ type: "SET_ERRORS", errors: newErrors });
          return;
        }
        
        await completeRegistration();
      } else {
        // Send OTP if form is valid and OTP not sent yet
        await sendOtp();
      }
    }
  };

  const handleGoogleLogin = async () => {
    try {
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      const userDocRef = doc(db, "users", user.uid);
      const userDocSnap = await getDoc(userDocRef);

      let userName = user.displayName || "";
      if (userDocSnap.exists()) {
        const data = userDocSnap.data();
        userName = data.name || userName;
      }
      setTimeout(() => {
        navigate("/dashboard");
      }, 50000);
    } catch (error) {
      console.error("Google login failed:", error);
      dispatch({
        type: "SET_FIREBASE_ERROR",
        error: "Google login failed. Please try again.",
      });
    }
  };

  const toggleLogin = () => {
    if (!isLogin && showOtpField) {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      localStorage.removeItem('otpSentTime');
      dispatch({ type: "RESET_OTP_FLOW" });
      setCanResend(false);
      setResendTimer(0);
    }
    dispatch({ type: "TOGGLE_LOGIN" });
  };

  useEffect(() => {
    if (loginResponse?.success === true) {
      navigate("/dashboard");
    }
  }, [loginResponse?.success]);

  useEffect(() => {
    if (signupResponse?.success === true) {
      toast.success("Registration successful! Please login.");
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      localStorage.removeItem('otpSentTime');
      dispatch({ type: "TOGGLE_LOGIN" });
      dispatch({ type: "RESET_OTP_FLOW" });
      setCanResend(false);
      setResendTimer(0);
    }
  }, [signupResponse?.success]);

  useEffect(() => {
    if (error?.message) {
      toast.error(error.message);
    }
  }, [error]);

  useEffect(() => {
    if (loginResponse?.data?.session?.access_token) {
      localStorage.setItem("userData", JSON.stringify(loginResponse.data));
    }
  }, [loginResponse]);

  // Render the resend button with timer
  const renderResendButton = () => {
    if (!showOtpField || !isOtpSent) return null;

    return (
      <div className="text-center mb-3">
        <Button
          variant="link"
          onClick={handleResendOtp}
          className={`text-decoration-none ${canResend ? 'text-primary' : 'text-secondary'}`}
          disabled={!canResend || isLoading}
          style={{
            cursor: canResend ? 'pointer' : 'not-allowed',
            opacity: canResend ? 1 : 0.6
          }}
        >
          {canResend ? (
            <>
              <FontAwesomeIcon icon={faRedo} className="me-2" />
              Resend OTP
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faClock} className="me-2" />
              Resend OTP in {formatTime(resendTimer)}
            </>
          )}
        </Button>
        {!canResend && (
          <div className="mt-2">
            <small className="text-muted">
              You can request a new OTP after the timer expires
            </small>
          </div>
        )}
      </div>
    );
  };

  return (
    <Container fluid className="login-container">
      {isLoading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}

      <Row className="justify-content-center">
        <Col md={6}></Col>
        <Col md={6}>
          <div className="fix-container">
            <div
              className="login-form text-center"
              style={{
                padding: isLogin ? "50px 15px 40px" : "30px 15px 40px",
                top: isLogin ? "50%" : "65%",
              }}
            >
              <div className="form-header mb-4">
                <img
                  src="https://www.dotandkey.com/cdn/shop/files/Vector_5.svg?v=1720438003&width=450"
                  className="w-50"
                  alt="logo"
                />
                <h1 className="text-center">Dot Connect</h1>
              </div>

              <Form onSubmit={handleSubmit}>
                <div className="email-grp">
                  {/* Show OTP field only during signup after OTP is sent */}
                  {!isLogin && showOtpField ? (
                    <>
                      <div className="mb-3 text-start">
                        <p className="mb-2">
                          OTP sent to <strong>{mail}</strong>
                        </p>
                        <p className="text-muted small mb-3">
                          Please check your email for the 6-digit OTP
                        </p>
                        
                        {/* Timer display */}
                        {/* {resendTimer > 0 && (
                          <div className="alert alert-info py-2 px-3 mb-3">
                            <div className="d-flex align-items-center justify-content-center">
                              <FontAwesomeIcon icon={faClock} className="me-2" />
                              <small>
                                You can resend OTP in <strong className="ms-1">{formatTime(resendTimer)}</strong>
                              </small>
                            </div>
                          </div>
                        )} */}
                      </div>
                      
                      <Form.Group className="mb-3">
                        <Form.Control
                          type="text"
                          value={otp}
                          maxLength={6}
                          isInvalid={!!errors.otp}
                          onChange={(e) => {
                            const onlyNums = e.target.value.replace(/\D/g, "").slice(0, 6);
                            dispatch({
                              type: "UPDATE_FIELD",
                              field: "otp",
                              value: onlyNums,
                            });
                          }}
                          placeholder="Enter 6-digit OTP"
                          className="text-center fs-5 letter-spacing-2"
                          style={{
                            letterSpacing: '0.5em',
                            paddingLeft: '1em'
                          }}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.otp}
                        </Form.Control.Feedback>
                      </Form.Group>

                      <div className="d-grid mb-3">
                        <Button type="submit" variant="dark" disabled={isLoading}>
                          {isLoading ? 'Processing...' : 'Register Now'}
                        </Button>
                      </div>

                      {renderResendButton()}
                    </>
                  ) : (
                    <>
                      <Form.Group className="mb-3">
                        <Form.Control
                          type="email"
                          value={mail}
                          isInvalid={!!errors.mail}
                          onChange={(e) =>
                            dispatch({
                              type: "UPDATE_FIELD",
                              field: "mail",
                              value: e.target.value,
                            })
                          }
                          placeholder="Enter email"
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.mail}
                        </Form.Control.Feedback>
                      </Form.Group>

                      {!isLogin && (
                        <>
                          <Form.Group className="mb-3">
                            <Form.Control
                              type="text"
                              value={name}
                              isInvalid={!!errors.name}
                              onChange={(e) =>
                                dispatch({
                                  type: "UPDATE_FIELD",
                                  field: "name",
                                  value: e.target.value,
                                })
                              }
                              placeholder="Enter name"
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.name}
                            </Form.Control.Feedback>
                          </Form.Group>

                          <Form.Group className="mb-3">
                            <Form.Control
                              type="text"
                              value={mob}
                              isInvalid={!!errors.mob}
                              onChange={(e) => {
                                const onlyNums = e.target.value.replace(/\D/g, "");
                                if (onlyNums.length <= 10) {
                                  dispatch({
                                    type: "UPDATE_FIELD",
                                    field: "mob",
                                    value: onlyNums,
                                  });
                                }
                              }}
                              placeholder="Enter mobile"
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.mob}
                            </Form.Control.Feedback>
                          </Form.Group>

                          <Form.Group className="mb-3">
                            <Form.Select
                              value={dept}
                              isInvalid={!!errors.dept || !!deptError}
                              onChange={handleDepartmentChange}
                            >
                              <option value="">Select Department</option>
                              {departments.map((d) => (
                                <option key={d.departmentId} value={d.departmentId}>
                                  {d.departmentName}
                                </option>
                              ))}
                            </Form.Select>
                            <Form.Control.Feedback type="invalid">
                              {errors.dept || deptError}
                            </Form.Control.Feedback>
                          </Form.Group>

                          <Form.Group className="mb-3">
                            <Form.Select
                              value={desg}
                              isInvalid={!!errors.desg || !!error?.desg}
                              onChange={(e) =>
                                dispatch({
                                  type: "UPDATE_FIELD",
                                  field: "desg",
                                  value: e.target.value,
                                })
                              }
                            >
                              <option value="">Select Designation</option>
                              {designations.map((d) => (
                                <option
                                  key={d.designationId}
                                  value={d.designationId}
                                >
                                  {d.designationName}
                                </option>
                              ))}
                            </Form.Select>
                            <Form.Control.Feedback type="invalid">
                              {errors.desg || error?.desg}
                            </Form.Control.Feedback>
                          </Form.Group>

                          <Form.Group className="mb-3">
                            <Form.Control
                              type="password"
                              value={pass}
                              isInvalid={!!errors.pass}
                              onChange={(e) =>
                                dispatch({
                                  type: "UPDATE_FIELD",
                                  field: "pass",
                                  value: e.target.value,
                                })
                              }
                              placeholder="Enter password"
                              autoComplete="new-password"
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.pass}
                            </Form.Control.Feedback>
                          </Form.Group>

                          <Form.Group className="mb-3">
                            <Form.Control
                              type="password"
                              value={conPass}
                              isInvalid={!!errors.conPass}
                              onChange={(e) =>
                                dispatch({
                                  type: "UPDATE_FIELD",
                                  field: "conPass",
                                  value: e.target.value,
                                })
                              }
                              placeholder="Confirm password"
                              autoComplete="new-password"
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.conPass}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </>
                      )}

                      {isLogin && (
                        <Form.Group className="mb-3">
                          <Form.Control
                            type="password"
                            value={pass}
                            isInvalid={!!errors.pass}
                            onChange={(e) =>
                              dispatch({
                                type: "UPDATE_FIELD",
                                field: "pass",
                                value: e.target.value,
                              })
                            }
                            placeholder="Enter password"
                            autoComplete="current-password"
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.pass}
                          </Form.Control.Feedback>
                        </Form.Group>
                      )}

                      <div className="d-grid mb-3">
                        <Button type="submit" variant="dark" disabled={isLoading}>
                          {isLogin ? (isLoading ? 'Logging in...' : 'Login') : (isLoading ? 'Sending OTP...' : 'Send OTP')}
                        </Button>
                      </div>
                    </>
                  )}
                </div>

                {firebaseErr && <p className="text-danger">{firebaseErr}</p>}

                <div className="text-center mb-3">
                  <Button
                    variant="light"
                    onClick={handleGoogleLogin}
                    className="w-100 d-none"
                  >
                    <FontAwesomeIcon icon={faGoogle} /> Login with Google
                  </Button>
                </div>

                <div className="text-center">
                  <p>
                    {isLogin
                      ? "Don't have an account?"
                      : "Already have an account?"}
                  </p>
                  <Button onClick={toggleLogin} className="togglr">
                    {isLogin ? "Signup" : "Login"}
                  </Button>
                </div>
              </Form>
            </div>
          </div>
        </Col>
      </Row>
    </Container>
  );
}